import useIsMounted from "ismounted";
import { useCallback, useEffect, useState } from "react";
import { ConnectDropTarget, useDrop } from "react-dnd";
import {
  Control,
  FormState,
  useForm,
  UseFormHandleSubmit,
  UseFormRegister,
} from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { ElectionType } from "../../../../models/election-type";
import { Rule } from "../../../../models/rule";
import { RuleSet, RuleSetUpdateRequest } from "../../../../models/ruleset";
import { State } from "../../../../models/state";
import { ElectionTypeService } from "../../../../services/election-type-service";
import { RuleService } from "../../../../services/rule-services";
import { RuleSetService } from "../../../../services/rule-set-services";
import { StateService } from "../../../../services/state-services";
import toast from 'react-hot-toast';

export type EditRuleSetState = {
  ruleset: RuleSet | undefined;
  currentRules: Rule[];
  availableRules: Rule[];
  filteredRules: Rule[];
  states: State[];
  electionTypes: ElectionType[];
  loading: boolean;
  saving: boolean;
  saveComplete: boolean;
  errorMessage: string;
  formState: FormState<RuleSetUpdateRequest>;
  control: Control<RuleSetUpdateRequest, object>;
  ruleSearch: string;
  register: UseFormRegister<RuleSetUpdateRequest>;
  handleSubmit: UseFormHandleSubmit<RuleSetUpdateRequest>;
  doSubmit: (data: RuleSetUpdateRequest) => Promise<void>;
  updateRuleSearch: (newValue: string) => void;
  cancel: () => void;
  drop: ConnectDropTarget;
  addRule: (rule: Rule) => void;
  deleteRule: (rule: Rule) => void;
  moveRule: (dragIndex: number, hoverIndex: number) => void;
  clearError: () => void;
};

type EditRuleSetProps = {
  rulesetId: string | undefined;
};

export default function useEditRuleset() {
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const { rulesetId } = useParams<EditRuleSetProps>();
  const [ruleset, setRuleSet] = useState<RuleSet>();
  const [currentRules, setCurrentRules] = useState<Rule[]>([]);
  const [availableRules, setAvailableRule] = useState<Rule[]>([]);
  const [filteredRules, setFilteredRules] = useState<Rule[]>([]);
  const [ruleSearch, setRuleSearch] = useState<string>("");
  const [states, setStates] = useState<State[]>([]);
  const [electionTypes, setElectionTypes] = useState<ElectionType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveComplete, setSaveComplete] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { register, setValue, handleSubmit, formState, control } =
    useForm<RuleSetUpdateRequest>({ reValidateMode: "onBlur", mode: "onBlur" });

  if (!rulesetId) {
    navigate("/rulesets");
  }

  const doSubmit = async (data: RuleSetUpdateRequest) => {
    try {
      setErrorMessage("");
      setSaving(true);
      await toast.promise(RuleSetService.updateRuleSet(rulesetId!, data), {
        loading: "Loading...",
        error: (e: any) => {
          setErrorMessage(e.message);
          setSaving(false);
          return e?.response?.data
            ? e.response.data
            : e?.message as string ?? 'An Error Occurred.'
        },
        success: () => {
          setSaveComplete(true);
          setSaving(false);
          return "Ruleset Saved Successfully"
        },
      })
    } catch (e: any) {
      console.log(e);
      setErrorMessage(e.message ?? e);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setErrorMessage("");
    setValue("name", ruleset!.name);
    setValue("stateId", ruleset!.state.id);
    setCurrentRules(ruleset!.rules.map((rule) => rule.rule));
    var ruleIds = ruleset!.rules.map((rule) => rule.rule.id);
    setValue("ruleIds", ruleIds);
    setValue("electionTypeId", ruleset!.electionType.id);
    setValue("isActive", ruleset!.isActive);
    setValue("isInternal", ruleset!.isInternal);
    navigate(-1);
  };

  const clearError = () => {
    setErrorMessage("");
  };

  const updateRuleSearch = (newValue: string) => {
    setRuleSearch(newValue);
  };

  const getRuleSet = useCallback(async () => {
    if (rulesetId) {
      try {
        setErrorMessage("");
        setLoading(true);
        const newStates = await StateService.getStates();
        setStates(newStates);
        const newElectionTypes = await ElectionTypeService.getElectionTypes();
        setElectionTypes(newElectionTypes);
        const availableRules = await RuleService.getRules();
        setAvailableRule(availableRules);
        const newRuleSet = await RuleSetService.getRuleSet(rulesetId);
        if (!newRuleSet) {
          navigate("/rulesets");
        } else {
          setRuleSet(newRuleSet);
          setValue("name", newRuleSet.name, { shouldValidate: true });
          setValue("stateId", newRuleSet.state.id, { shouldValidate: true });
          setValue("electionTypeId", newRuleSet.electionType.id, {
            shouldValidate: true,
          });
          setValue("isActive", newRuleSet.isActive, { shouldValidate: true });
          setValue("isInternal", newRuleSet.isInternal, {
            shouldValidate: true,
          });
          setCurrentRules(newRuleSet.rules.map((rule) => rule.rule));
          var ruleIds = newRuleSet.rules.map((rule) => rule.rule.id);
          setValue("ruleIds", ruleIds, { shouldValidate: true });
        }
      } catch (e: any) {
        console.log(e);
        setErrorMessage(e.message ?? e);
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    } else {
      console.log("in the bad place");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesetId, navigate]);

  useEffect(() => {
    getRuleSet();
  }, [getRuleSet]);

  useEffect(() => {
    if (saveComplete) {
      setTimeout(() => {
        navigate("/rulesets");
      }, 500);
    }
  }, [saveComplete, navigate]);

  useEffect(() => {
    var tempList = [...availableRules];

    tempList = tempList.filter(
      (rule) =>
        currentRules.length === 0 ||
        currentRules.map((r) => r.id).indexOf(rule.id) < 0
    );

    if (ruleSearch) {
      tempList = tempList.filter((rule) => {
        return (
          rule.name
            .toLocaleLowerCase()
            .indexOf(ruleSearch.toLocaleLowerCase()) >= 0
        );
      });
    }
    setFilteredRules(tempList);
  }, [availableRules, currentRules, ruleSearch]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [{ canDrop, isOver }, drop] = useDrop(
    () => ({
      accept: "rule",
      drop: () => ({}),
      collect: (monitor: any) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    []
  );

  const addRule = (rule: Rule) => {
    setCurrentRules((args) => [...args, rule]);
  };

  useEffect(() => {
    var ruleIds = [...currentRules.map((rule) => rule.id)];
    setValue("ruleIds", ruleIds, {
      shouldValidate: true,
    });
  }, [currentRules, setValue]);

  const deleteRule = (rule: Rule) => {
    const deleteIndex = currentRules.indexOf(rule);
    currentRules.splice(deleteIndex, 1);
    setCurrentRules((arg) => [...currentRules]);
  };

  const moveRule = (dragIndex: number, hoverIndex: number) => {
    const dragCard = currentRules[dragIndex];
    currentRules.splice(dragIndex, 1);
    currentRules.splice(hoverIndex, 0, dragCard);
    setCurrentRules((arg) => [...currentRules]);
  };

  return {
    ruleset,
    currentRules,
    availableRules,
    filteredRules,
    states,
    electionTypes,
    loading,
    saving,
    saveComplete,
    formState,
    errorMessage,
    control,
    ruleSearch,
    register,
    handleSubmit,
    doSubmit,
    cancel,
    drop,
    addRule,
    deleteRule,
    moveRule,
    updateRuleSearch,
    clearError,
  };
}
